import { z } from 'zod';
import { dotNetDateSchema, dotNetDateToIso, numericEnum } from '@/shared/utils/api/serverPrimitives';
import type {
  Baseline,
  BaselineStage,
  DiffItem,
  DiffQuery,
  MergeRequestDiff,
  ModelBaselines,
  Participant,
  ProductionRequestInput,
  RevertInput,
} from '../types/baseline.types';

/**
 * Wire contract for the baselines, diff and participants routes. These are planned in Core
 * (see the Conbent baselines plan) and are not in the committed OpenAPI description yet, so
 * they are checked by the contract tests in this repository until Core ships them. Enums
 * stay numeric, like the rest of `api/v1/merge-model-requests`.
 */

const baselineStage = numericEnum('BaselineStage', { 1: 'Shared', 2: 'Published' } as const);
const baselineOrigin = numericEnum('BaselineOrigin', { 1: 'Merged', 2: 'Reverted' } as const);
const changeKind = numericEnum('ChangeKind', { 1: 'Added', 2: 'Removed', 3: 'Modified' } as const);
const diffStatus = numericEnum('DiffStatus', {
  1: 'Pending',
  2: 'Ready',
  3: 'Failed',
  4: 'NotApplicable',
} as const);

export const baselineSchema = z.object({
  id: z.string(),
  modelId: z.string(),
  stage: baselineStage.schema,
  requestId: z.string().nullish(),
  originRequestId: z.string(),
  modelVersion: z.number().int(),
  sourceSnapshotId: z.number().int(),
  fileSha256: z.string(),
  fileSizeBytes: z.number(),
  revisionCode: z.string().nullish(),
  origin: baselineOrigin.schema,
  restoredFromBaselineId: z.string().nullish(),
  isCurrent: z.boolean(),
  setAtUtc: dotNetDateSchema,
  setBy: z.string(),
});

export const modelBaselinesSchema = z.object({
  shared: baselineSchema.nullable(),
  published: baselineSchema.nullable(),
  history: z.array(baselineSchema),
});

const parameterChangeSchema = z.object({
  name: z.string(),
  oldValue: z.string().nullish(),
  newValue: z.string().nullish(),
});

const diffItemSchema = z.object({
  uniqueId: z.string(),
  kind: changeKind.schema,
  category: z.string(),
  typeName: z.string(),
  parameterChanges: z.array(parameterChangeSchema),
});

export const mergeRequestDiffSchema = z.object({
  status: diffStatus.schema,
  summary: z
    .object({ added: z.number().int(), removed: z.number().int(), modified: z.number().int(), truncated: z.boolean() })
    .nullable(),
  items: z
    .object({
      items: z.array(diffItemSchema),
      pageIndex: z.number().int(),
      pageSize: z.number().int(),
      totalCount: z.number().int(),
    })
    .nullable(),
});

export const participantListSchema = z.array(z.object({ userId: z.string(), email: z.string().nullish() }));

export function toBaseline(b: z.output<typeof baselineSchema>): Baseline {
  return {
    id: b.id,
    modelId: b.modelId,
    stage: b.stage,
    requestId: b.requestId ?? null,
    originRequestId: b.originRequestId,
    modelVersion: b.modelVersion,
    sourceSnapshotId: b.sourceSnapshotId,
    fileSha256: b.fileSha256,
    fileSizeBytes: b.fileSizeBytes,
    revisionCode: b.revisionCode ?? null,
    origin: b.origin,
    restoredFromBaselineId: b.restoredFromBaselineId ?? null,
    isCurrent: b.isCurrent,
    setAt: dotNetDateToIso(b.setAtUtc),
    setBy: b.setBy,
  };
}

export function toModelBaselines(m: z.output<typeof modelBaselinesSchema>): ModelBaselines {
  return {
    shared: m.shared === null ? null : toBaseline(m.shared),
    published: m.published === null ? null : toBaseline(m.published),
    history: m.history.map(toBaseline),
  };
}

export function toMergeRequestDiff(d: z.output<typeof mergeRequestDiffSchema>): MergeRequestDiff {
  return {
    status: d.status,
    summary: d.summary,
    items:
      d.items === null
        ? null
        : {
            items: d.items.items.map(
              (i): DiffItem => ({
                uniqueId: i.uniqueId,
                kind: i.kind,
                category: i.category,
                typeName: i.typeName,
                parameterChanges: i.parameterChanges.map((p) => ({
                  name: p.name,
                  oldValue: p.oldValue ?? null,
                  newValue: p.newValue ?? null,
                })),
              })
            ),
            totalCount: d.items.totalCount,
            page: d.items.pageIndex,
            pageSize: d.items.pageSize,
          },
  };
}

export function toParticipants(list: z.output<typeof participantListSchema>): readonly Participant[] {
  return list.map((p) => ({ userId: p.userId, email: p.email ?? null }));
}

const STAGE_WIRE: Readonly<Record<BaselineStage, number>> = { Shared: 1, Published: 2 };
const KIND_WIRE = { Added: 1, Removed: 2, Modified: 3 } as const;

export function diffQueryString(query: DiffQuery): string {
  const params = new URLSearchParams({ pageIndex: String(query.page), pageSize: String(query.pageSize) });
  if (query.kind !== undefined) params.set('kind', String(KIND_WIRE[query.kind]));
  return params.toString();
}

export function revertBody(input: RevertInput): Record<string, unknown> {
  return {
    targetBaselineId: input.targetBaselineId,
    expectedCurrentBaselineId: input.expectedCurrentBaselineId,
    reason: input.reason.trim(),
  };
}

/** A Shared to Published request names only the baseline: Core derives the model and file from it. */
export function productionRequestBody(input: ProductionRequestInput): Record<string, unknown> {
  return {
    requestedTransition: 2,
    sourceBaselineId: input.sourceBaselineId,
    reviewerIds: input.reviewerIds,
    authorizerId: input.authorizerId,
  };
}

export const stageWire = (stage: BaselineStage): number => STAGE_WIRE[stage];
